import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Input,
  signal
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { codeToHtml } from 'shiki';

import { StepperFormStore } from '../../../+state/firmware-package/stepper-form.store';
import { formateManifestFromBackend } from '../../../helpers/formatManifestFromBackend';
import { readXmlFromZip } from '../../../helpers/readXmlFromZip';

@Component({
  selector: 'remote-update-domain-summary',
  templateUrl: './summary.html',
  styleUrls: ['./summary.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    CommonModule,
    MatRadioModule,
    MatButtonToggleModule,
    FormsModule,
    MatCheckboxModule
  ]
})
export class Summary {
  @Input() currentSection = '';
  private sanitizer = inject(DomSanitizer);
  private store = inject(StepperFormStore);

  public allFormData = computed(() => this.store.getAllFormData());
  extractedXmlContent = signal<SafeHtml | null>(null);

  isEditingFirmwarePackge = computed(() => this.store.isEditingFirmwarePackage);

  constructor() {
    effect(() => {
      const data = this.allFormData();

      const isAllValid =
        data.fileUpload.isValid &&
        data.manifestInfo.isValid &&
        data.packageMetadata.isValid &&
        data.deviceCompatibility.isValid &&
        data.distribution &&
        data.releaseNotes.isValid;

      this.store.updateSummary(isAllValid);

      const file = data.fileUpload.file;
      if (file) {
        this.loadAndRenderManifest(file);
      }

      if (this.isEditingFirmwarePackge()) {
        const manifestContent = formateManifestFromBackend(data.manifestInfo.manifestName);
        this.renderHighlightedXml(manifestContent);
      }
    });
  }

  private async loadAndRenderManifest(selectedFile: File) {
    const xmlContent = await readXmlFromZip(selectedFile);
    await this.renderHighlightedXml(xmlContent);
  }

  private async renderHighlightedXml(xmlContent: string) {
    const highlightedXml = await codeToHtml(xmlContent, {
      lang: 'xml',
      theme: 'light-plus'
    });

    this.extractedXmlContent.set(this.sanitizer.bypassSecurityTrustHtml(highlightedXml));
  }
}
